import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactSelect from 'react-select';
import {
  Box, Stack, Text, useToast, Flex, Spinner, Button,
  FormControl, FormLabel, HStack, Alert, AlertIcon, AlertTitle,
  AlertDescription, useColorModeValue, Badge, Divider, SimpleGrid,
  Card, CardBody, Heading, Wrap, Tag, TagLabel, TagCloseButton,
  Menu, MenuButton, MenuList, MenuItemOption, MenuOptionGroup,
  Portal, Select, Tooltip,
} from '@chakra-ui/react';

import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import { FiSave, FiRefreshCw, FiDatabase, FiUpload, FiChevronDown, FiX, FiInfo } from 'react-icons/fi';
import axiosInstance from 'axiosInstance';
import baseUrl from 'Base_Url/baseUrl';

import SchemeSelector from '../UploadSection/components/SchemeSelector';
import FileTypeSelector from '../UploadSection/components/FileTypeSelector';

ModuleRegistry.registerModules([AllCommunityModule]);

// ─── CONFIGURATION ───────────────────────────────────────────────────────────
// Add filetype IDs that should show empty records (50 blank rows)
const EMPTY_RECORDS_FILETYPES = [
  "68ef914e7b08ceea860b06b7",
  "68d13b7289a0f69943a07ef5",
  "691aaf910f739804e0aeac60",
  "691ae0c60f739804e0aeac85",
  "6917266bfb3ca3c1c4c7ee69",
  "68d13b7289a0f69943a07ef5",
  "691aacde0f739804e0aeac5b",
  "6917237efb3ca3c1c4c7ea85",
  "68ef8b587b08ceea860b0688",
  "68ef908e7b08ceea860b06b2",
  "68ef8e907b08ceea860b06a3",
  "68ef8f447b08ceea860b06a8",
  "68ef8d937b08ceea860b069e",
  "68ef90177b08ceea860b06ad",
  "68ef8ca97b08ceea860b0699"

];

const Schemes_With_Hide_ShowButton = [
  "68ee2171ad9b09bdda13b4f6",
]

// ─── FORMULA SERVICE ─────────────────────────────────────────────────────────

class FormulaService {
  constructor(schemaData) {
    this.formulas = schemaData
      .filter(col => col.isFormula === true && col.formula)
      .map(col => ({
        targetColumn: col.key,
        expression: col.formula,
        dependencies: col.formulaDependencies || [],
        displayExpression: col.formulaDisplay || col.formula,
        order: col.formulaOrder || 0
      }));
  }

  evaluateFormula(expression, rowData) {
    try {
      let evaluableExpression = expression;

      const columnMatches = expression.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
      const operators = ['+', '-', '*', '/', '(', ')', '%', 'Math', 'Number', 'parseFloat'];

      columnMatches.forEach(col => {
        if (!operators.includes(col) && !['true', 'false', 'null', 'undefined'].includes(col)) {
          let value = rowData[col];

          if (value === null || value === undefined || value === '') {
            value = 0;
          } else if (typeof value === 'string') {
            value = parseFloat(value) || 0;
          } else if (typeof value === 'number') {
            value = isNaN(value) ? 0 : value;
          } else {
            value = Number(value) || 0;
          }

          evaluableExpression = evaluableExpression.replace(new RegExp(`\\b${col}\\b`, 'g'), value);
        }
      });

      const calculate = new Function('return (' + evaluableExpression + ')');
      const result = calculate();

      return Math.round(result * 100) / 100;
    } catch (error) {
      console.error('Formula evaluation error:', error, 'Expression:', expression);
      return 0;
    }
  }

  applyFormulasToRow(rowData) {
    if (this.formulas.length === 0) return rowData;

    const result = { ...rowData };
    const sortedFormulas = [...this.formulas].sort((a, b) => a.order - b.order);

    for (const formula of sortedFormulas) {
      const calculatedValue = this.evaluateFormula(formula.expression, result);
      result[formula.targetColumn] = calculatedValue;
    }

    return result;
  }

  applyFormulasToRows(rows) {
    return rows.map(row => this.applyFormulasToRow(row));
  }

  isFormulaColumn(columnKey) {
    return this.formulas.some(f => f.targetColumn === columnKey);
  }

  getFormulaForColumn(columnKey) {
    return this.formulas.find(f => f.targetColumn === columnKey);
  }

  getDependentFormulas(columnKey) {
    return this.formulas.filter(f => f.dependencies.includes(columnKey));
  }
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const isMonthKey = (key) => key === 'month';

const isLocationKey = (key, col = null) => {
  if (col?.validation?.isEditable === true) {
    return false;
  }
  return [
    'taluka', 'tahsil', 'village', 'division',
    'district', 'circle', 'subdivision', 'subDivision', 'subdivisionMain'
  ].includes(key);
};

const isFormulaKey = (col) => col.isFormula === true;


const isAlphabetOnlyTextField = (col) => col.key === 'monthOfGrantDisbursement';



const dateToString = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};


const stringToDate = (val) => {
  if (!val || val === '') return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  const parts = String(val).split('-');
  if (parts.length === 3) {
    const d = new Date(parts[2], parts[1] - 1, parts[0]);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const DropdownCellRenderer = (params) => {
  const value = params.value;
  const colField = params.colDef.field;
  const [hovered, setHovered] = useState(false);

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    params.node.setDataValue(colField, '');
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%' }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1 }}>
        {value}
      </span>
      {value !== null && value !== undefined && value !== '' && (
        <button
          className="clear-btn"
          onClick={handleClear}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px',
            marginRight: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: hovered ? '#e53e3e' : '#a0aec0',
            transition: 'color 0.2s',
          }}
          title="Clear value"
        >
          <FiX size={14} className="clear-btn" />
        </button>
      )}
    </div>
  );
};

const buildDynamicColumnDefs = (schemaData, formulaService) => {
  const serialColumn = {
    headerName: 'SR.NO',
    field: '__sn__',
    width: 60,
    pinned: 'left',
    sortable: false,
    editable: false,
    valueGetter: (params) => params.node.rowIndex + 1,
    cellStyle: { fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #e0e0e0' },
    headerTooltip: 'Serial Number',
  };

  const dataColumns = schemaData.map((col) => {
    const key = col.key;
    const isMonth = isMonthKey(key);
    const isLocation = isLocationKey(key, col);
    const isFormula = isFormulaKey(col);
    const schemaEditable = col.validation?.isEditable === true;
    const schemaLocked = col.validation?.isLocked === true;

    const isGridEditable = !isFormula && schemaEditable && !schemaLocked && !isMonth && !isLocation;

    const columnType = col.type?.toLowerCase();
    const validationType = col.validation?.type;

    const isNumberField = columnType === 'number' || validationType === 'number';
    const isDateField = validationType === 'date' || columnType === 'date';

    const isTextField = isAlphabetOnlyTextField(col);

    const cellBg = isFormula
      ? '#e8f5e9'
      : isMonth
        ? '#e6f7ff'
        : isLocation
          ? '#f0f9f4'
          : !isGridEditable
            ? '#f5f5f5'
            : undefined;

    const cellColor = isFormula
      ? '#2e7d32'
      : isMonth
        ? '#0050b3'
        : isLocation
          ? '#2c7a7a'
          : !isGridEditable
            ? '#666666'
            : undefined;

    const columnDef = {
      headerName: col.displayName,
      field: key,
      width: isMonth ? 150 : isLocation ? 200 : (isFormula ? 200 : 180),
      editable: isGridEditable,
      sortable: true,
      resizable: true,
      _isNumberField: isNumberField,
      _isDateField: isDateField,
      _isTextField: isTextField,
      cellStyle: {
        borderRight: '1px solid #e0e0e0',
        ...(cellBg && { backgroundColor: cellBg }),
        ...(cellColor && { color: cellColor }),
        ...((isFormula || !isGridEditable) && { fontStyle: 'italic' }),
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return '';
        if (isDateField) {
          if (params.value instanceof Date) return dateToString(params.value);
          return params.value;
        }
        if (isFormula && typeof params.value === 'number') {
          return params.value.toLocaleString();
        }
        if (isNumberField && params.value === '') return '';
        return params.value;
      },
    };

    if (isNumberField && isGridEditable) {
      columnDef.cellEditor = 'agNumberCellEditor';

      columnDef.valueParser = (params) => {
        const value = params.newValue;
        if (value === null || value === undefined || value === '') {
          return '';
        }
        const numValue = Number(value);
        return isNaN(numValue) ? '' : numValue;
      };

      columnDef.suppressKeyboardEvent = (params) => {
        const { event } = params;
        if (event.key === '+' || event.key === '-' || event.key === 'e' || event.key === 'E') {
          event.preventDefault();
          event.stopPropagation();
          return true;
        }
        return false;
      };
    }

    if (isTextField && isGridEditable) {
      columnDef.cellEditor = 'agTextCellEditor';

      columnDef.valueSetter = (params) => {
        let newValue = params.newValue;
        if (newValue && typeof newValue === 'string') {
          newValue = newValue.replace(/[^A-Za-z\s]/g, '');
        }
        params.data[params.colDef.field] = newValue ?? '';
        return true;
      };

      columnDef.suppressKeyboardEvent = (params) => {
        const { event } = params;
        const key = event.key;

        const allowedKeys = [
          'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
          'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
          'Home', 'End', 'F2', ' '
        ];

        if (allowedKeys.includes(key)) return false;

        if (event.ctrlKey || event.metaKey) return false;

        const isAlphabet = /^[A-Za-z]$/.test(key);
        const isSpace = key === ' ';

        if (!isAlphabet && !isSpace) {
          event.preventDefault();
          event.stopPropagation();
          return true;
        }

        return false;
      };

      return columnDef;
    }

    if (isGridEditable) {
      if (validationType === 'dropdown' && col.dropdownOptions?.length) {
        let dropdownValues = col.dropdownOptions;

        if (dropdownValues.length > 0 && typeof dropdownValues[0] === 'object') {
          dropdownValues = dropdownValues.map(opt => opt.name || opt.label || opt.displayName || String(opt));
        } else {
          dropdownValues = dropdownValues.map(opt => String(opt));
        }

        columnDef.cellEditor = 'agSelectCellEditor';
        columnDef.cellEditorParams = { values: dropdownValues };
        columnDef.cellEditorPopup = false;
        columnDef.singleClickEdit = true;
        columnDef.cellRenderer = DropdownCellRenderer;
        columnDef.cellRendererParams = {
          suppressMouseEventHandling: (params) => {
            const target = params.event.target;
            return !!(target && (target.closest('.clear-btn') || target.classList.contains('clear-btn')));
          }
        };
      } else if (isDateField) {
        columnDef.cellEditor = 'agDateCellEditor';
        columnDef.valueGetter = (params) => {
          const val = params.data?.[key];
          if (!val || val === '') return null;
          if (val instanceof Date) return val;
          return stringToDate(val);
        };
        columnDef.valueParser = (params) => {
          if (!params.newValue) return '';
          if (params.newValue instanceof Date) {
            return dateToString(params.newValue);
          }
          return params.newValue;
        };
        columnDef.singleClickEdit = true;
      }
    }
    return columnDef;
  });

  return [serialColumn, ...dataColumns];
};

// ─── ROW FORMATTER ────────────────────────────────────────────────────────────
const formatDynamicRows = (rows, schemaData, prefillInfo, financialYear, selectedMonth, includeMonth) => {
  return rows.map((row, idx) => {
    const rowObj = {};

    if (row.godownId) {
      rowObj.godownId = row.godownId;
    }
    if (row.godown) {
      rowObj.godown = row.godown;
    }
    if (row.tahsilId) {
      rowObj.tahsilId = row.tahsilId;
    }
    if (row.tahsil) {
      rowObj.tahsil = row.tahsil;
    }
    if (row.district) {
      rowObj.district = row.district;
    }
    if (row.division) {
      rowObj.division = row.division;
    }

    Object.keys(row).forEach(key => {
      if (key.includes('Id') || key === '_id' || key === 'id') {
        if (!rowObj[key]) {
          rowObj[key] = row[key];
        }
      }
    });

    schemaData.forEach((col) => {
      const key = col.key;
      const schemaEditable = col.validation?.isEditable === true;
      const schemaLocked = col.validation?.isLocked === true;
      const isMonth = isMonthKey(key);
      const isLocation = isLocationKey(key);
      const isFormula = isFormulaKey(col);
      let rawValue = row[key];

      const columnType = col.type?.toLowerCase();
      const validationType = col.validation?.type;
      const isNumberField = columnType === 'number' || validationType === 'number';

      if (isMonth) {
        rowObj[key] = includeMonth ? (selectedMonth || prefillInfo?.selectedMonth || '') : '';
        return;
      }

      if (key === 'previousFY') {
        rowObj[key] = rawValue || prefillInfo?.previousFY || financialYear;
        return;
      }
      if (key === 'currentFY') {
        rowObj[key] = rawValue || prefillInfo?.currentFY || financialYear;
        return;
      }

      if (isLocation) {
        let value = rawValue ?? '';
        if (!value && key === 'taluka') value = row.tahsil ?? '';
        if (!value && key === 'village') value = row.village ?? '';
        if (!value && key === 'subDivision') value = row.subdivisionMain ?? row.subDivision ?? '';
        rowObj[key] = value;
        return;
      }

      if (isFormula) {
        rowObj[key] = (rawValue === null || rawValue === undefined || rawValue === '') ? 0 : Number(rawValue);
        return;
      }

      if (schemaEditable && !schemaLocked) {
        if (rawValue === null || rawValue === undefined || rawValue === '') {
          rowObj[key] = '';
        } else {
          if (isNumberField && typeof rawValue === 'string' && rawValue !== '') {
            const numValue = Number(rawValue);
            rowObj[key] = isNaN(numValue) ? rawValue : numValue;
          } else {
            rowObj[key] = rawValue;
          }
        }
        return;
      }

      rowObj[key] = (rawValue === null || rawValue === undefined) ? '' : rawValue;
    });

    return rowObj;
  });
};

// ─── GENERATE EMPTY ROWS ────────────────────────────────────────────────────

const generateEmptyRows = (schemaData, numberOfRows = 100, prefillInfo = {}, financialYear = '', month = '') => {
  const rows = [];

  for (let i = 0; i < numberOfRows; i++) {
    const emptyRow = {};

    schemaData.forEach(col => {
      const key = col.key;
      const isFormula = isFormulaKey(col);
      const isMonth = isMonthKey(key);
      const isLocation = isLocationKey(key);
      const columnType = col.type?.toLowerCase();
      const validationType = col.validation?.type;
      const isNumberField = columnType === 'number' || validationType === 'number';

      if (isFormula) {
        emptyRow[key] = 0;
      } else if (isMonth) {
        emptyRow[key] = month || '';
      } else if (key === 'previousFY') {
        emptyRow[key] = prefillInfo?.previousFY || financialYear;
      } else if (key === 'currentFY') {
        emptyRow[key] = prefillInfo?.currentFY || financialYear;
      } else if (isLocation) {
        emptyRow[key] = '';
      } else {
        const defaultValue = col.defaultValue || col.validation?.defaultValue;

        if (defaultValue !== undefined && defaultValue !== null) {
          emptyRow[key] = defaultValue;
        } else if (isNumberField) {
          emptyRow[key] = '';
        } else if (columnType === 'date' || validationType === 'date') {
          emptyRow[key] = '';
        } else {
          emptyRow[key] = '';
        }
      }
    });

    rows.push(emptyRow);
  }

  return rows;
};

// ─── INLINE TOOLTIP COMPONENT ─────────────────────────────────────────────────

const InvalidKeyTooltip = ({ visible, message, position }) => {
  if (!visible) return null;

  return (
    <Box
      position="fixed"
      top={`${position.y}px`}
      left={`${position.x}px`}
      zIndex={9999}
      pointerEvents="none"
      style={{ transform: 'translateY(-110%)' }}
    >
      <Box
        bg="red.600"
        color="white"
        px={3}
        py={1.5}
        borderRadius="md"
        fontSize="xs"
        fontWeight="600"
        boxShadow="lg"
        whiteSpace="nowrap"
        display="flex"
        alignItems="center"
        gap={1}
      >
        <Box
          position="absolute"
          bottom="-6px"
          left="14px"
          w={0}
          h={0}
          borderLeft="6px solid transparent"
          borderRight="6px solid transparent"
          borderTop="6px solid"
          borderTopColor="red.600"
        />
        ⚠️ {message}
      </Box>
    </Box>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

function ExcelGridDataEntry() {
  const toast = useToast();
  const gridRef = useRef();
  const gridContainerRef = useRef(null);
  const bgColor = useColorModeValue('white', 'gray.800');

  // Selection
  const [scheme, setScheme] = useState('');
  const [fileType, setFileType] = useState('');
  const [selectedFileTypeObj, setSelectedFileTypeObj] = useState(null);
  const [selectedTahsilIds, setSelectedTahsilIds] = useState([]);
  const [selectedGodownIds, setSelectedGodownIds] = useState([]);
  const [financialYear, setFinancialYear] = useState('');
  const [month, setMonth] = useState('');

  // Grid
  const [columnDefs, setColumnDefs] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [originalRowData, setOriginalRowData] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [schema, setSchema] = useState([]);
  const [prefillInfo, setPrefillInfo] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [formulaService, setFormulaService] = useState(null);
  const [hasExistingDataWithSave, setHasExistingDataWithSave] = useState(false);

  // ── Invalid-key tooltip state ──
  const [invalidKeyTooltip, setInvalidKeyTooltip] = useState({
    visible: false,
    message: '',
    position: { x: 0, y: 0 },
  });
  const invalidKeyTooltipTimerRef = useRef(null);

  // Dropdown options
  const [schemes, setSchemes] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const [tahsils, setTahsils] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [months, setMonths] = useState([]);

  // Loading
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [loadingFileTypes, setLoadingFileTypes] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [loadingSdoUsers, setLoadingSdoUsers] = useState(false);

  const [sdoUsers, setSdoUsers] = useState([]);
  const [selectedSdoUser, setSelectedSdoUser] = useState(null);

  const SDO_SCHEME_ID = '68d135b1e18cfc3e5248edc2';
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  // Auto-save
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(false);
  const [recoveredData, setRecoveredData] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const [loginRole, setLoginRole] = useState('');

  // ─── Derived flags ────────────────────────────────────────────────────────

  const getStartingYear = useCallback((fy) => {
    if (!fy) return '';
    const match = fy.match(/^(\d{4})/);
    return match ? match[1] : fy;
  }, []);

  const shouldUseGodownAPI = useCallback(() => {
    return scheme === '68b952cdfa74b7a86b7aa3c63' && fileType === '693ba9e0c09fd08f5f517afd3';
  }, [scheme, fileType]);

  const hasMonthSupport = useCallback(() => {
    return selectedFileTypeObj?.hasMonth === true;
  }, [selectedFileTypeObj]);

  const schemaHasMonth = useCallback(() => {
    return schema.some(col => isMonthKey(col.key));
  }, [schema]);

  const schemaHasFormulas = useCallback(() => {
    return schema.some(col => isFormulaKey(col));
  }, [schema]);

  const shouldShowEmptyRecords = useCallback(() => {
    return EMPTY_RECORDS_FILETYPES.includes(fileType);
  }, [fileType]);

  // ─── Storage ──────────────────────────────────────────────────────────────

  const getStorageKey = useCallback(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsed = JSON.parse(userData);
        const userId = parsed._id || parsed.id || parsed.email || 'unknown';
        return `gridAutoSave_${userId}_${scheme || 'noScheme'}_${fileType || 'noFileType'}`;
      }
    } catch {
      // ignore
    }
    return 'gridAutoSave_anonymous';
  }, [scheme, fileType]);

  const saveToLocalStorage = useCallback(() => {
    if (!autoSaveEnabled || !showGrid) return;
    const saveData = {
      rowData, originalRowData, columnDefs, schema, headers,
      scheme, fileType, selectedTahsilIds, selectedGodownIds,
      financialYear, month, hasUnsavedChanges, prefillInfo,
      timestamp: new Date().toISOString(),
      lastActivity: Date.now(),
    };
    localStorage.setItem(getStorageKey(), JSON.stringify(saveData));
    setLastSaved(new Date());
  }, [
    rowData, originalRowData, columnDefs, schema, headers, scheme, fileType,
    selectedTahsilIds, selectedGodownIds, financialYear, month,
    hasUnsavedChanges, autoSaveEnabled, showGrid, getStorageKey, prefillInfo,
  ]);

  const loadFromLocalStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(getStorageKey());
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      const isRecent = (Date.now() - parsed.lastActivity) < 24 * 60 * 60 * 1000;
      if (isRecent && parsed.rowData?.length > 0) return parsed;
    } catch {
      // ignore
    }
    return null;
  }, [getStorageKey]);

  const clearLocalStorage = useCallback(() => {
    localStorage.removeItem(getStorageKey());
    setShowRecoveryPrompt(false);
    setRecoveredData(null);
    setLastSaved(null);
  }, [getStorageKey]);

  const recoverSavedData = useCallback((saved) => {
    if (!saved) return;
    setRowData(saved.rowData);
    setOriginalRowData(saved.originalRowData);
    setColumnDefs(saved.columnDefs);
    setSchema(saved.schema);
    setHeaders(saved.headers);
    setScheme(saved.scheme);
    setFileType(saved.fileType);
    setSelectedTahsilIds(saved.selectedTahsilIds || []);
    setSelectedGodownIds(saved.selectedGodownIds || []);
    setFinancialYear(saved.financialYear);
    setMonth(saved.month);
    setPrefillInfo(saved.prefillInfo);
    setHasUnsavedChanges(saved.hasUnsavedChanges);
    setShowGrid(true);
    if (gridApi) {
      setTimeout(() => {
        gridApi.setGridOption('rowData', saved.rowData);
        gridApi.sizeColumnsToFit();
      }, 100);
    }
    toast({
      title: 'Data Recovered',
      description: `Restored from ${new Date(saved.timestamp).toLocaleString()}`,
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    clearLocalStorage();
  }, [toast, clearLocalStorage, gridApi]);

  // ─── Auto-save effects ────────────────────────────────────────────────────

  useEffect(() => {
    if (!autoSaveEnabled || !showGrid || !hasUnsavedChanges) return;
    const timer = setTimeout(saveToLocalStorage, 3000);
    return () => clearTimeout(timer);
  }, [rowData, hasUnsavedChanges, showGrid, autoSaveEnabled, saveToLocalStorage]);

  useEffect(() => {
    const handle = (e) => {
      if (hasUnsavedChanges && autoSaveEnabled) {
        saveToLocalStorage();
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Data saved locally.';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handle);
    return () => window.removeEventListener('beforeunload', handle);
  }, [hasUnsavedChanges, autoSaveEnabled, saveToLocalStorage]);

  useEffect(() => {
    if (!autoSaveEnabled || !showGrid || !hasUnsavedChanges) return;
    const interval = setInterval(saveToLocalStorage, 30000);
    return () => clearInterval(interval);
  }, [autoSaveEnabled, showGrid, hasUnsavedChanges, saveToLocalStorage]);

  // Check for recoverable data on mount
  useEffect(() => {
    const saved = loadFromLocalStorage();
    if (saved) {
      setRecoveredData(saved);
      setShowRecoveryPrompt(true);
    }
  }, [loadFromLocalStorage]);

  // Online/offline
  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      toast({ title: 'Connection Restored', status: 'success', duration: 3000 });
    };
    const onOffline = () => {
      setIsOnline(false);
      saveToLocalStorage();
      toast({
        title: 'Connection Lost',
        description: 'Data saved locally',
        status: 'warning',
        duration: 4000,
      });
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, [hasUnsavedChanges, saveToLocalStorage, toast]);

  // Clean up tooltip timer on unmount
  useEffect(() => {
    return () => {
      if (invalidKeyTooltipTimerRef.current) {
        clearTimeout(invalidKeyTooltipTimerRef.current);
      }
    };
  }, []);

  // ─── Init ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const startYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    const years = [0].map(i => {
      const y = startYear + i;
      return `${y}-${(y + 1).toString().slice(-2)}`;
    });
    setFinancialYears(years);
    setFinancialYear(`${startYear}-${(startYear + 1).toString().slice(-2)}`);
  }, []);

  useEffect(() => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    setMonths(monthNames);
    setMonth(monthNames[new Date().getMonth()]);
  }, []);


  const getCurrentUserData = useCallback(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsed = JSON.parse(userData);
        setLoginRole(parsed.role);
        return parsed;
      }
    } catch { /* ignore */ }
    return null;
  }, []);

  // ─── API calls ────────────────────────────────────────────────────────────

  const fetchSchemes = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      setLoadingSchemes(true);
      const res = await axiosInstance.get(`${baseUrl}/master/all-scheme`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 200) {
        const all = res?.data?.data?.data || [];
        setSchemes(all);
        if (all.length === 1) setScheme(all[0]._id);
      }
    } catch {
      toast({ title: 'Error fetching schemes', status: 'error', duration: 3000 });
    } finally {
      setLoadingSchemes(false);
    }
  }, [toast]);

  const fetchFileTypes = useCallback(async (schemeId) => {
    const token = localStorage.getItem('token');
    if (!schemeId) return;
    try {
      setLoadingFileTypes(true);
      const res = await axiosInstance.get(`${baseUrl}/master/all-file-type`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scheme: schemeId },
      });
      if (res.data.status) {
        const fetched = res.data?.data?.data || [];
        setFileTypes(fetched);
        if (fetched.length === 1) {
          setFileType(fetched[0]._id);
          setSelectedFileTypeObj(fetched[0]);
        }
      }
    } catch {
      setFileTypes([]);
    } finally {
      setLoadingFileTypes(false);
    }
  }, []);

  const fetchSdoUsers = useCallback(async () => {
    // console.log('Fetching SDO users for scheme:', scheme);
    if (!scheme || scheme !== SDO_SCHEME_ID || fileType === '68d13b7289a0f69943a07ef5') return;
    setLoadingSdoUsers(true);
    try {
      const token = localStorage.getItem('token');
        const params = fileType ? { fileType } : {};
      const response = await axiosInstance.get(`${baseUrl}/upload-file/sdo-users/${scheme}`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      const responseData = response.data?.data;
      if (responseData?.success || response.data?.status === true) {
        setSdoUsers(responseData?.users || []);
      } else {
        setSdoUsers([]);
      }
    } catch (error) {
      console.error('Error fetching SDO users:', error);
      toast({
        title: 'Error loading SDO offices',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setSdoUsers([]);
    } finally {
      setLoadingSdoUsers(false);
    }
  }, [scheme, fileType, toast]);

  useEffect(() => {
    if (scheme === SDO_SCHEME_ID && fileType !== '68d13b7289a0f69943a07ef5') {
      fetchSdoUsers();
    } else {
      setSdoUsers([]);
      setSelectedSdoUser(null);
    }
  }, [scheme, fileType, fetchSdoUsers]);
  const fetchTahsils = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (shouldUseGodownAPI()) return;
    try {
      const params = { fileTypeId: fileType, schemeId: scheme, financialYear };
      if (hasMonthSupport() && month) params.month = month;
      const res = await axiosInstance.get(`${baseUrl}/master/check-approved-tahsils`, {
        params, headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 200 && res.data.status) {
        setTahsils(res?.data?.data?.data || []);
        setSelectedTahsilIds([]);
      }
    } catch (err) {
      console.error('Error fetching tahsils:aaaaaaaaa', err);
    }
  }, [fileType, scheme, financialYear, month, hasMonthSupport, shouldUseGodownAPI]);

  const fetchGodowns = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!shouldUseGodownAPI()) return;
    try {
      const res = await axiosInstance.get(`${baseUrl}/pds/godown/get-godown`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 200 && res.data.status === true) {
        setGodowns(res?.data?.data || []);
      }
    } catch (err) {
      console.error('Error fetching godowns:', err);
    }
  }, [shouldUseGodownAPI]);

  // ─── Invalid-key tooltip helpers ──────────────────────────────────────────

  const showInvalidKeyTooltip = useCallback((message, cellElement) => {
    if (invalidKeyTooltipTimerRef.current) {
      clearTimeout(invalidKeyTooltipTimerRef.current);
    }

    let x = 200;
    let y = 200;

    if (cellElement) {
      const rect = cellElement.getBoundingClientRect();
      x = rect.left + rect.width / 2 - 80;
      y = rect.top;
    }

    setInvalidKeyTooltip({ visible: true, message, position: { x, y } });

    invalidKeyTooltipTimerRef.current = setTimeout(() => {
      setInvalidKeyTooltip(prev => ({ ...prev, visible: false }));
    }, 2500);
  }, []);

  const isInvalidNumberKey = useCallback((key, ctrlKey, metaKey, altKey) => {
    if (ctrlKey || metaKey) return false;

    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'F2',
    ];
    if (allowedKeys.includes(key)) return false;

    if (/^\d$/.test(key)) return false;

    if (key === '.') return false;

    return true;
  }, []);

  // ─── Grid with Formula Support ────────────────────────────────────────────

  const handleShowGrid = async () => {
    const token = localStorage.getItem('token');
    if (!scheme || !fileType) {
      toast({ title: 'Please select scheme and file type', status: 'warning' });
      return;
    }

    const useGodown = shouldUseGodownAPI();
    const isEmptyRecordsMode = shouldShowEmptyRecords();

    if (!isEmptyRecordsMode) {
      if (
        selectedFileTypeObj?.hasTaluka === true &&
        selectedFileTypeObj?.isTalukaObjectId === true &&
        !useGodown &&
        selectedTahsilIds.length === 0
      ) {
        toast({ title: 'Please select at least one taluka', status: 'warning', duration: 3000 });
        return;
      }

      if (useGodown && selectedGodownIds.length === 0) {
        toast({ title: 'Please select at least one godown', status: 'warning', duration: 3000 });
        return;
      }
    }

    clearLocalStorage();
    setHasExistingDataWithSave(false);
    setLoadingGrid(true);
    setShowGrid(false);

    try {
      let schemaData;
      let rows;
      let prefillData;

      if (isEmptyRecordsMode) {
        const requestData = {
          schemeId: scheme,
          financialYear: getStartingYear(financialYear),
        };
        if (hasMonthSupport() && month) requestData.month = month;

        if (useGodown) {
          requestData.godownIds = selectedGodownIds;
        } else if (selectedFileTypeObj?.scenario?.toLowerCase().includes('sdo')) {
          requestData.userId = selectedTahsilIds;
          requestData.userIds = selectedTahsilIds;
          requestData.tahsilIds = selectedTahsilIds;
        } else {
          requestData.tahsilIds = selectedTahsilIds;
        }

        const response = await axiosInstance.post(
          `${baseUrl}/master/${fileType}/template?format=json`,
          requestData,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success && response.data?.data) {
          schemaData = response.data.data.schema;
          prefillData = response.data.data.prefillInfo;

          const numberOfRecords = 100;
          rows = generateEmptyRows(schemaData, numberOfRecords, prefillData, financialYear, month);

          toast({
            title: 'Empty Grid Created',
            description: `${numberOfRecords} empty records ready for data entry`,
            status: 'info',
            duration: 3000,
          });
        } else {
          throw new Error('Failed to load schema');
        }
      } else {
        const requestData = {
          schemeId: scheme,
          financialYear: getStartingYear(financialYear),
        };
        if (hasMonthSupport() && month) requestData.month = month;

        if (useGodown) {
          requestData.godownIds = selectedGodownIds;
        } else if (selectedFileTypeObj?.scenario?.toLowerCase().includes('sdo')) {
          requestData.userId = selectedTahsilIds;
          requestData.userIds = selectedTahsilIds;
          requestData.tahsilIds = selectedTahsilIds;
        } else {
          requestData.tahsilIds = selectedTahsilIds;
        }

        const response = await axiosInstance.post(
          `${baseUrl}/master/${fileType}/template?format=json`,
          requestData,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success && response.data?.data) {
          schemaData = response.data.data.schema;
          rows = response.data.data.rows;
          prefillData = response.data.data.prefillInfo;

          const existingDataWithSave = response.data.data.metadata?.hasExistingDataWithSave === true;
          setHasExistingDataWithSave(existingDataWithSave);
        } else {
          throw new Error('Failed to load grid data');
        }
      }

      if (schemaData && rows) {
        console.log('Raw schema from API:', schemaData);
        console.log('Rows data:', rows);

        setSchema(schemaData);
        setPrefillInfo(prefillData);
        setHeaders(schemaData.map(col => col.displayName));

        const formulaSvc = new FormulaService(schemaData);
        setFormulaService(formulaSvc);

        const includeMonth = hasMonthSupport();
        const selectedMonthValue = includeMonth ? month : null;

        let formattedRows = formatDynamicRows(
          rows, schemaData, prefillData,
          getStartingYear(financialYear),
          selectedMonthValue,
          includeMonth
        );

        if (formulaSvc.formulas.length > 0) {
          formattedRows = formulaSvc.applyFormulasToRows(formattedRows);
          console.log('Formulas applied to rows');
        }

        console.log('Final formatted rows:', formattedRows);

        const dynamicColumns = buildDynamicColumnDefs(schemaData, formulaSvc);
        setColumnDefs(dynamicColumns);

        setRowData(formattedRows);
        setOriginalRowData(JSON.parse(JSON.stringify(formattedRows)));
        setHasUnsavedChanges(false);
        setShowGrid(true);

        const formulaCount = formulaSvc.formulas.length;
        const editableCount = schemaData.filter(c =>
          c.validation?.isEditable === true && !isFormulaKey(c) && !isMonthKey(c.key)
        ).length;

        toast({
          title: isEmptyRecordsMode ? 'Empty Grid Loaded Successfully' : 'Grid Loaded Successfully',
          description: `${formattedRows.length} rows loaded. ${editableCount} editable columns. ${formulaCount} auto-calculated formula columns.`,
          status: 'success',
          duration: 4000,
        });
      }
    } catch (err) {
      console.error('Error loading grid:', err);
      toast({
        title: 'Error loading grid',
        description: err.response?.data?.message || err.message,
        status: 'error',
        duration: 5000
      });
    } finally {
      setLoadingGrid(false);
    }
  };


  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
    params.api.sizeColumnsToFit();
  }, []);

  const syncRowDataFromGrid = useCallback(() => {
    if (!gridApi) return;
    const updated = [];
    gridApi.forEachNode((node) => {
      updated.push({ ...node.data });
    });
    return updated;
  }, [gridApi]);

  // ─── Cell key-down handler ─────────────────────────────────────────────────

  const onCellKeyDown = useCallback((params) => {
    const { event, colDef } = params;
    if (!event) return;

    const isNumberField = colDef?._isNumberField === true;
    if (!isNumberField) return;

    const focusedCell = gridApi?.getFocusedCell();
    if (!focusedCell) return;

    const { key, ctrlKey, metaKey, altKey } = event;

    if (isInvalidNumberKey(key, ctrlKey, metaKey, altKey)) {
      event.preventDefault();
      event.stopPropagation();

      const cellElement = event.target?.closest('.ag-cell') || event.target;

      showInvalidKeyTooltip(
        `Only numbers are allowed `,
        cellElement
      );
    }
  }, [gridApi, isInvalidNumberKey, showInvalidKeyTooltip]);

  // ─── Cell value changed with formula recalculation ────────────────────────

  const onCellValueChanged = useCallback((params) => {
    const { colDef, data, newValue, oldValue, node } = params;

    if (oldValue === newValue) return;
    if (isMonthKey(colDef.field)) return;

    if (formulaService?.isFormulaColumn(colDef.field)) {
      toast({
        title: 'Formula Column',
        description: 'This column is auto-calculated and cannot be edited directly',
        status: 'warning',
        duration: 2000,
      });
      node.setDataValue(colDef.field, oldValue);
      return;
    }

    if (colDef?._isTextField === true && newValue && typeof newValue === 'string') {
      const alphabetRegex = /^[A-Za-z\s]*$/;
      if (!alphabetRegex.test(newValue)) {
        toast({
          title: 'Invalid Input',
          description: `${colDef.headerName} should only contain alphabets and spaces`,
          status: 'error',
          duration: 3000,
        });
        node.setDataValue(colDef.field, oldValue);
        return;
      }

      const cleanedValue = newValue.replace(/[^A-Za-z\s]/g, '');
      if (cleanedValue !== newValue) {
        node.setDataValue(colDef.field, cleanedValue);
        toast({
          title: 'Invalid Characters Removed',
          description: `${colDef.headerName} should only contain alphabets and spaces`,
          status: 'warning',
          duration: 2000,
        });
        return;
      }
    }

    let processedNewValue = newValue;
    if (colDef?._isDateField === true && newValue instanceof Date) {
      processedNewValue = dateToString(newValue);
    }

    let updatedRow = { ...data, [colDef.field]: processedNewValue };

    if (formulaService && formulaService.formulas.length > 0) {
      const dependentFormulas = formulaService.getDependentFormulas(colDef.field);

      if (dependentFormulas.length > 0) {
        updatedRow = formulaService.applyFormulasToRow(updatedRow);

        toast({
          title: "Formulas Updated",
          description: `${dependentFormulas.length} formula(s) recalculated automatically`,
          status: "info",
          duration: 1500,
        });
      }
    }

    const newRowData = [...rowData];
    newRowData[node.rowIndex] = updatedRow;
    setRowData(newRowData);
    setHasUnsavedChanges(true);

    if (gridApi) {
      gridApi.setGridOption('rowData', newRowData);
    }
  }, [formulaService, rowData, gridApi, toast]);

  // ─── Cell editing stopped ─────────────────────────────────────────────────

  const onCellEditingStopped = useCallback((params) => {
    if (isMonthKey(params.colDef.field)) return;
    if (formulaService?.isFormulaColumn(params.colDef.field)) return;
    setHasUnsavedChanges(true);
  }, [formulaService]);

  // ─── Paste handler with formula protection ────────────────────────────────

  useEffect(() => {
    const container = gridContainerRef.current;
    if (!container || !gridApi || !showGrid) return;

    const handlePaste = (e) => {
      e.preventDefault();
      const clipText = e.clipboardData?.getData('text/plain');
      if (!clipText || !gridApi) return;

      const pastedRows = clipText
        .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
        .split('\n')
        .map(row => row.split('\t'))
        .filter(row => row.some(cell => cell.trim() !== ''));

      if (!pastedRows.length) return;

      const focusedCell = gridApi.getFocusedCell();
      if (!focusedCell) {
        toast({ title: 'Click a cell first before pasting', status: 'warning', duration: 2000 });
        return;
      }

      const allColumns = gridApi.getColumns()?.filter(c => c.getColDef().field !== '__sn__') || [];
      const startRowIndex = focusedCell.rowIndex;
      const startColIndex = allColumns.findIndex(c => c === focusedCell.column);
      if (startColIndex === -1) return;

      let pastedCount = 0;
      let updatedData = JSON.parse(JSON.stringify(rowData));
      let formulasRecalculated = false;

      pastedRows.forEach((pastedRow, rowOffset) => {
        const rowIndex = startRowIndex + rowOffset;
        if (rowIndex >= updatedData.length) return;

        let rowAffected = false;

        pastedRow.forEach((cellValue, colOffset) => {
          const colIndex = startColIndex + colOffset;
          if (colIndex >= allColumns.length) return;

          const col = allColumns[colIndex];
          const colDef = col?.getColDef();
          const field = colDef?.field;
          if (!field) return;

          if (formulaService?.isFormulaColumn(field)) {
            toast({
              title: 'Cannot paste into Formula column',
              description: `${colDef.headerName} is auto-calculated`,
              status: 'warning',
              duration: 2000,
            });
            return;
          }

          if (isMonthKey(field)) {
            toast({
              title: 'Cannot paste into Month column',
              description: 'Month is auto-populated from the filter',
              status: 'warning',
              duration: 2000,
            });
            return;
          }

          const columnSchema = schema.find(s => s.key === field);
          if (isLocationKey(field, columnSchema)) return;
          const schemaEditable = columnSchema?.validation?.isEditable === true;
          const schemaLocked = columnSchema?.validation?.isLocked === true;
          if (!schemaEditable || schemaLocked) return;

          let value = cellValue.trim();
          const columnType = columnSchema?.type?.toLowerCase();
          const validationType = columnSchema?.validation?.type;

          if (value === '') {
            updatedData[rowIndex][field] = '';
            pastedCount++;
            rowAffected = true;
            return;
          }

          if (columnSchema && isAlphabetOnlyTextField(columnSchema)) {
            const cleanedValue = value.replace(/[^A-Za-z\s]/g, '');
            if (cleanedValue !== value) {
              toast({
                title: 'Invalid Characters Removed',
                description: `${colDef.headerName} only accepts alphabets and spaces`,
                status: 'warning',
                duration: 2000,
              });
            }
            value = cleanedValue;
            if (value === '') return;
          } else if (columnType === 'number' || validationType === 'number') {
            const numValue = parseFloat(value);
            if (isNaN(numValue)) {
              toast({
                title: 'Invalid Number',
                description: `"${value}" is not valid for ${colDef.headerName}`,
                status: 'warning',
                duration: 2000,
              });
              return;
            }
            value = numValue;
          }

          if (validationType === 'date' || columnType === 'date') {
            if (!/^\d{2}-\d{2}-\d{4}$/.test(value)) {
              const parsed = new Date(value);
              if (!isNaN(parsed.getTime())) {
                value = dateToString(parsed);
              }
            }
          }

          updatedData[rowIndex][field] = value;
          pastedCount++;
          rowAffected = true;
        });

        if (rowAffected && formulaService && formulaService.formulas.length > 0) {
          updatedData[rowIndex] = formulaService.applyFormulasToRow(updatedData[rowIndex]);
          formulasRecalculated = true;
        }
      });

      if (pastedCount > 0) {
        setRowData(updatedData);
        // NOTE: originalRowData is intentionally NOT updated here.
        // It must remain as the last-saved/loaded baseline so that
        // "Discard Changes" correctly reverts paste operations.
        setHasUnsavedChanges(true);
        gridApi.setGridOption('rowData', updatedData);
        gridApi.refreshCells({ force: true });

        toast({
          title: 'Paste Successful',
          description: `Updated ${pastedCount} cell(s)${formulasRecalculated ? '. Formulas recalculated.' : ''}`,
          status: 'success',
          duration: 1500
        });
      }
    };

    container.addEventListener('paste', handlePaste);
    return () => container.removeEventListener('paste', handlePaste);
  }, [gridApi, showGrid, schema, toast, rowData, formulaService]);

  // ─── Save ─────────────────────────────────────────────────────────────────

  const handleSaveNow = async (action) => {
    const token = localStorage.getItem('token');

    if (gridApi) {
      gridApi.stopEditing();
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    if (!scheme || !fileType) {
      toast({ title: 'Please select scheme and file type', status: 'warning' });
      return;
    }

    setSaving(true);
    setUploadProgress(0);

    try {
      const useGodown = shouldUseGodownAPI();
      const includeMonth = hasMonthSupport();
      const isEmptyRecordsMode = shouldShowEmptyRecords();
      const records = [];

      let currentRowData = rowData;
      if (formulaService && formulaService.formulas.length > 0) {
        currentRowData = formulaService.applyFormulasToRows(currentRowData);
      }

      const talukaColumn = schema.find(col => col.key === 'taluka');
      const isTalukaObjectId = talukaColumn?.type === 'ObjectId' || talukaColumn?.validation?.dataType === 'objectid';

      for (let index = 0; index < currentRowData.length; index++) {
        const row = currentRowData[index];
        const rowDataObj = {};
        let hasData = false;

        schema.forEach((col) => {
          const key = col.key;
          const val = row[key];
          const isFormula = isFormulaKey(col);
          const isEditable = col.validation?.isEditable === true;
          const isLocked = col.validation?.isLocked === true;
          const isDateField =
            col.validation?.type === 'date' || col.type?.toLowerCase() === 'date';
          const isNumberField =
            col.type?.toLowerCase() === 'number' || col.validation?.type === 'number';

          if (key === 'previousFY') {
            rowDataObj[key] = prefillInfo?.previousFY || financialYear;
          } else if (key === 'currentFY') {
            rowDataObj[key] = prefillInfo?.currentFY || financialYear;
          } else if (isMonthKey(key) && includeMonth) {
            rowDataObj[key] = month;
          } else if (isFormula) {
            rowDataObj[key] = val !== undefined && val !== null && val !== '' ? Number(val) : 0;
          } else if (isEditable && !isLocked && !isMonthKey(key)) {
            if (val === '' || val === null || val === undefined) {
              rowDataObj[key] = isNumberField ? 0 : '';
            } else {
              rowDataObj[key] = isDateField && val instanceof Date ? dateToString(val) : val;

              const stringValue = String(val).trim();
              if (stringValue !== '' && stringValue !== '0') {
                hasData = true;
              }
            }
          } else {
            rowDataObj[key] = val !== undefined && val !== null ? val : '';

            const stringValue = String(val ?? '').trim();
            if (stringValue !== '' && stringValue !== '0') {
              hasData = true;
            }
          }
        });

        if (isEmptyRecordsMode && !hasData) {
          continue;
        }

        let locationId = null;
        let tahsilId = null;
        let talukaName = null;

        if (useGodown) {
          if (row.godownId) {
            locationId = row.godownId;
          }
          else if (row._id && godowns.some((g) => g._id === row._id)) {
            locationId = row._id;
          }
          else {
            const godownName = row.godown || rowDataObj.godown;
            if (godownName) {
              const godownObj = godowns.find(
                (g) =>
                  g.placeOfGodown === godownName ||
                  g.name === godownName ||
                  g._id === godownName
              );
              if (godownObj) locationId = godownObj._id;
            }
          }

          if (!locationId && selectedGodownIds.length > 0) {
            locationId = selectedGodownIds[0];
          }

          if (!locationId) {
            console.error(`Row ${index}: No godownId found, skipping`);
            toast({
              title: `Missing Godown ID for row ${index + 1}`,
              description: `Godown: ${row.godown || rowDataObj.godown}`,
              status: 'error',
              duration: 3000,
            });
            continue;
          }
        } else {
          const talukaValue = rowDataObj.taluka || rowDataObj.tahsil || row.taluka || row.tahsil;

          if (talukaValue && talukaValue !== '') {
            if (isTalukaObjectId && talukaColumn?.dropdownOptions) {
              const foundTaluka = talukaColumn.dropdownOptions.find(
                opt => opt.name === talukaValue || opt.id === talukaValue || opt._id === talukaValue
              );

              if (foundTaluka) {
                tahsilId = foundTaluka.id || foundTaluka._id;
                talukaName = foundTaluka.name;
              }
            }

            if (!tahsilId && tahsils && tahsils.length > 0) {
              const tahsilObj = tahsils.find((t) => t.name === talukaValue);
              if (tahsilObj) {
                tahsilId = tahsilObj._id;
                talukaName = tahsilObj.name;
              }
            }

            if (!tahsilId && !isTalukaObjectId) {
              talukaName = talukaValue;
            }

            if (!locationId && tahsilId) {
              locationId = tahsilId;
            }
          }

          if (!tahsilId && !locationId && selectedTahsilIds.length > 0) {
            locationId = selectedTahsilIds[0];
            tahsilId = selectedTahsilIds[0];
          }
        }

        const record = {
          schemeId: scheme,
          fileTypeId: fileType,
          financialYear: financialYear,
          rowIndex: index,
          ...(includeMonth && { month }),
          ...rowDataObj,
          uploadedAt: new Date().toISOString(),
        };

        if (isTalukaObjectId) {
          if (tahsilId) {
            record.tahsilId = tahsilId;
            if (talukaName) {
              record.taluka = talukaName;
            } else if (rowDataObj.taluka && typeof rowDataObj.taluka !== 'object') {
              record.taluka = rowDataObj.taluka;
            }
          } else if (locationId) {
            record.tahsilId = locationId;
          }
          if ((rowDataObj.taluka || rowDataObj.tahsil) && !record.tahsilId) {
            console.warn(`Row ${index}: Taluka "${rowDataObj.taluka || rowDataObj.tahsil}" found but no matching ID`);
            record.taluka = rowDataObj.taluka || rowDataObj.tahsil;
          }
        } else {
          if (locationId && !useGodown) {
            record.tahsilId = locationId;
          }
        }

        if (useGodown && locationId) {
          record.godownId = locationId;
        } else if (!useGodown && !isTalukaObjectId && locationId) {
          record.tahsilId = locationId;
        }

        records.push(record);
      }

      console.log(`Total records prepared for saving: ${records.length}`);

      if (records.length === 0) {
        toast({
          title: 'No data to save',
          description: isEmptyRecordsMode
            ? 'No rows contain any data. Please fill in at least one row before saving.'
            : 'All rows are empty.',
          status: 'warning',
          duration: 3000,
        });
        setSaving(false);
        return;
      }

      const response = await axiosInstance.post(
        `${baseUrl}/upload-file/upload`,
        {
          data: records,
          schemeId: scheme,
          fileTypeId: fileType,
          financialYear: financialYear,
          ...(includeMonth && { month }),
          totalRecords: records.length,
          actionType: action,
          ...(scheme === SDO_SCHEME_ID && selectedSdoUser && fileType !== '68d13b7289a0f69943a07ef5' && { subDivisionId: selectedSdoUser.value }),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          onUploadProgress: (pe) =>
            setUploadProgress(Math.round((pe.loaded * 100) / pe.total)),
        }
      );

      console.log(response, "---------------response------------");

      if (response.data.status === true) {
        toast({
          title: action === 'upload' ? 'Data Uploaded Successfully' : 'Data Saved Successfully',
          description: `${records.length} row(s) saved successfully`,
          status: 'success',
          duration: 4000,
        });
        setOriginalRowData(JSON.parse(JSON.stringify(currentRowData)));
        setUploadProgress(0);

        if (action === 'upload') {
          setShowGrid(false);
          fetchTahsils();
        }
        clearLocalStorage();
      } else if (response.data.code === 409 || response.data?.data?.includes('already exists')) {
        toast({
          title: 'Upload Failed',
          description: response.data?.data || response.data?.message || 'Data already exists for this period',
          status: 'error',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Save Failed',
          description: response.data?.message || response.data?.data || 'Failed to save data',
          status: 'error',
          duration: 5000,
        });
      }
    } catch (err) {
      console.error('Save error:', err);

      if (err.response?.status === 409) {
        toast({
          title: 'Upload Failed',
          description: err.response?.data?.data || err.response?.data?.message || 'Data already exists for this period',
          status: 'error',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Save Failed',
          description: err.response?.data?.message || err.message,
          status: 'error',
          duration: 5000,
        });
      }
    } finally {
      setSaving(false);
      setTimeout(() => setUploadProgress(0), 3000);
    } 
  };

  const handleDiscard = () => {
    setRowData(JSON.parse(JSON.stringify(originalRowData)));
    if (gridApi) gridApi.setGridOption('rowData', originalRowData);
    setHasUnsavedChanges(false);
    clearLocalStorage();
    toast({ title: 'Changes discarded', status: 'info', duration: 2000 });
  };

  // ─── Selection helpers ────────────────────────────────────────────────────

  const handleTalukaChange = (values) => setSelectedTahsilIds(values);
  const handleSelectAllTalukas = () => {
    const selectable = tahsils.filter(t => t.status === 'pending').map(t => t._id);
    setSelectedTahsilIds(selectedTahsilIds.length === selectable.length ? [] : selectable);
  };
  const handleClearTalukas = () => setSelectedTahsilIds([]);

  const handleGodownChange = (values) => setSelectedGodownIds(values);
  const handleSelectAllGodowns = () => setSelectedGodownIds(
    selectedGodownIds.length === godowns.length ? [] : godowns.map(g => g._id)
  );
  const handleClearGodowns = () => setSelectedGodownIds([]);

  const getSelectedTalukaNames = () => selectedTahsilIds.map(id => tahsils.find(t => t._id === id)?.name).filter(Boolean);
  const getSelectedGodownNames = () => selectedGodownIds.map(id => {
    const g = godowns.find(g => g._id === id);
    return g?.placeOfGodown || g?.name;
  }).filter(Boolean);

  // ─── Load effects ─────────────────────────────────────────────────────────

  useEffect(() => { getCurrentUserData(); fetchSchemes(); }, [getCurrentUserData, fetchSchemes]);
  useEffect(() => { if (scheme) fetchFileTypes(scheme); }, [scheme, fetchFileTypes]);
  useEffect(() => {
    if (fileType && fileTypes.length) {
      setSelectedFileTypeObj(fileTypes.find(ft => ft._id === fileType) || null);
    }
  }, [fileType, fileTypes]);
  useEffect(() => {
    if (scheme && fileType) {
      if (shouldUseGodownAPI()) fetchGodowns();
      else fetchTahsils();
    }
  }, [scheme, fileType, shouldUseGodownAPI, fetchGodowns, fetchTahsils, financialYear, month]);

  const useGodown = shouldUseGodownAPI();
  const showMonthFilter = hasMonthSupport();

  // ─── Recovery Alert ───────────────────────────────────────────────────────

  const RecoveryAlert = () => {
    if (!showRecoveryPrompt || !recoveredData) return null;
    return (
      <Alert status="warning" variant="solid" mb={4} borderRadius="lg" bg="orange.500" color="white">
        <AlertIcon />
        <Box flex={1}>
          <AlertTitle fontSize="sm">Unsaved Data Found!</AlertTitle>
          <AlertDescription fontSize="xs">
            From {new Date(recoveredData.timestamp).toLocaleString()} — would you like to restore it?
          </AlertDescription>
        </Box>
        <HStack spacing={2}>
          <Button size="xs" colorScheme="whiteAlpha" onClick={() => recoverSavedData(recoveredData)}>Restore</Button>
          <Button size="xs" colorScheme="whiteAlpha" variant="outline" onClick={clearLocalStorage}>Discard</Button>
        </HStack>
      </Alert>
    );
  };

  return (
    <Box p={4}>
      {/* ── Floating invalid-key tooltip ── */}
      <InvalidKeyTooltip
        visible={invalidKeyTooltip.visible}
        message={invalidKeyTooltip.message}
        position={invalidKeyTooltip.position}
      />

      <Card variant="outline" bg={bgColor} borderRadius="xl">
        <CardBody>
          <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
            <Heading size="lg" color="teal.700">Data Entry Grid</Heading>

          </Flex>
          <Divider mb={4} />

          <RecoveryAlert />

          {/* Filters */}
          <Stack spacing={3} mb={4}>
            <Flex gap={3} align="end" wrap="nowrap">

              <FormControl w="230px">
                <FormLabel fontSize="xs" fontWeight="600">
                  Scheme
                </FormLabel>

                <SchemeSelector
                  schemes={schemes}
                  scheme={scheme}
                  loadingSchemes={loadingSchemes}
                  loginRole={loginRole}
                  handleSchemeChange={(opt) => {
                    setScheme(opt?.value || '');
                    setFileType('');
                    setShowGrid(false);
                    setHasUnsavedChanges(false);
                  }}
                  mapOptions={(arr) => arr.map(i => ({ value: i._id, label: i.name }))}
                  cleanLabel={(text) => text || 'Unnamed'}
                  shortenName={(text) =>
                    text?.length > 30 ? text.substring(0, 27) + '...' : text
                  }
                />
              </FormControl>

              <FormControl w="270px">
                <FormLabel fontSize="xs" fontWeight="600">
                  File Type
                </FormLabel>

                <FileTypeSelector
                  fileTypes={fileTypes}
                  fileType={fileType}
                  scheme={scheme}
                  loadingFileTypes={loadingFileTypes}
                  handleFileTypeChange={(opt) => {
                    setFileType(opt?.value || '');
                    setShowGrid(false);
                    setHasUnsavedChanges(false);
                  }}
                  mapOptions={(arr) => arr.map(i => ({ value: i._id, label: i.name }))}
                  shortenName={(text) =>
                    text?.length > 30 ? text.substring(0, 27) + '...' : text
                  }
                />
              </FormControl>

              <FormControl w="150px">
                <FormLabel fontSize="xs" fontWeight="600">
                  Financial Year
                </FormLabel>

                <Select
                  size="sm"
                  value={financialYear}
                  onChange={(e) => setFinancialYear(e.target.value)}
                >
                  {financialYears.map((fy) => (
                    <option key={fy} value={fy}>
                      {fy}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {showMonthFilter && (
                <FormControl w="150px">
                  <FormLabel fontSize="xs" fontWeight="600">
                    <HStack spacing={1}>
                      <Text>Month</Text>

                      <Tooltip
                        label="Selecting a month auto-populates the Month column in all rows"
                        hasArrow
                      >
                        <Box as="span" color="blue.500">
                          ⓘ
                        </Box>
                      </Tooltip>
                    </HStack>
                  </FormLabel>

                  <Select
                    size="sm"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    {months.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

            </Flex>

            {/* SDO Filter (Specific Scheme) */}
            {scheme === SDO_SCHEME_ID && fileType && fileType !== '68d13b7289a0f69943a07ef5' && (
              <Box bg={bgColor} p={4} borderRadius="xl" shadow="sm" borderWidth="1px" borderColor="gray.100" mb={4}>
                <Flex justify="space-between" align="center" mb={3}>
                  <HStack>
                    <FiDatabase size="14px" color="#2c7a7a" />
                    <Text fontWeight="600" fontSize="sm">Select SDO Office</Text>
                  </HStack>
                </Flex>
                <ReactSelect
                  options={sdoUsers.map(u => ({ value: u.subDivision?._id || u._id, label: u.nameOfOffice || u.name, user: u }))}
                  value={selectedSdoUser}
                  onChange={(selected) => setSelectedSdoUser(selected)}
                  placeholder="Search & Select SDO Office..."
                  isClearable
                  isLoading={loadingSdoUsers}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      borderColor: '#E2E8F0',
                      '&:hover': { borderColor: '#CBD5E0' },
                    })
                  }}
                />
              </Box>
            )}

            {/* Taluka / Godown selector - Hide for empty records mode */}
            {scheme && fileType && !shouldShowEmptyRecords() && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {(useGodown || (selectedFileTypeObj?.hasTaluka === true && selectedFileTypeObj?.isTalukaObjectId === true)) && (
                  <Card p={2} variant="outline">
                    <Stack spacing={2}>
                      <Flex justify="space-between" align="center">
                        <HStack>
                          <FiDatabase size="14px" color="#2c7a7a" />
                          <Text fontWeight="600" fontSize="sm">
                            {useGodown ? `Select Godowns (${godowns.length})` : `Select Talukas (${tahsils.length})`}
                          </Text>
                          {(useGodown ? selectedGodownIds : selectedTahsilIds).length > 0 && (
                            <Badge colorScheme="teal">
                              {(useGodown ? selectedGodownIds : selectedTahsilIds).length} selected
                            </Badge>
                          )}
                        </HStack>
                        {/* {loginRole === 'schemeAdmin' && ( */}
                        <HStack>
                          <Button size="xs" variant="ghost" onClick={useGodown ? handleSelectAllGodowns : handleSelectAllTalukas}>
                            {useGodown
                              ? (selectedGodownIds.length === godowns.length ? 'Deselect All' : 'Select All')
                              : (() => {
                                const pendingIds = tahsils.filter(t => t.status === 'pending').map(t => t._id);
                                const allPendingSelected = pendingIds.length > 0 && pendingIds.every(id => selectedTahsilIds.includes(id));
                                return allPendingSelected ? 'Deselect All' : 'Select All';
                              })()
                            }
                          </Button>
                          <Button size="xs" variant="ghost" colorScheme="red" onClick={useGodown ? handleClearGodowns : handleClearTalukas}>Clear</Button>
                        </HStack>
                        {/* )} */}
                      </Flex>

                      {useGodown ? (
                        <Menu closeOnSelect={false}>
                          <MenuButton as={Button} rightIcon={<FiChevronDown />} size="sm" variant="outline">
                            {selectedGodownIds.length === 0 ? 'Select godowns...' : `${selectedGodownIds.length} selected`}
                          </MenuButton>
                          <Portal>
                            <MenuList maxH="300px" overflowY="auto">
                              <MenuOptionGroup type="checkbox" value={selectedGodownIds.map(String)} onChange={handleGodownChange}>
                                {godowns.map(g => <MenuItemOption key={g._id} value={g._id}>{g.placeOfGodown || g.name}</MenuItemOption>)}
                              </MenuOptionGroup>
                            </MenuList>
                          </Portal>
                        </Menu>
                      ) : (
                        <Menu closeOnSelect={false}>
                          <MenuButton as={Button} rightIcon={<FiChevronDown />} size="sm" variant="outline">
                            {selectedTahsilIds.length === 0 ? 'Select talukas...' : `${selectedTahsilIds.length} selected`}
                          </MenuButton>
                          <Portal>
                            <MenuList maxH="400px" overflowY="auto">
                              <Box px={3} py={2} borderBottomWidth="1px" bg="gray.50">
                                <Flex justify="space-between">
                                  <Text fontSize="sm" fontWeight="600">Select Talukas</Text>
                                  <HStack spacing={2}>
                                    <Badge colorScheme="green">✅ {tahsils.filter(t => t.status === 'Approve').length} Approved</Badge>
                                    <Badge colorScheme="orange">📝 {tahsils.filter(t => t.status === 'pending').length} Pending</Badge>
                                    <Badge colorScheme="blue">📨 {tahsils.filter(t => t.status === 'submitted').length} Submitted</Badge>
                                  </HStack>
                                </Flex>
                              </Box>
                              <MenuOptionGroup type="checkbox" value={selectedTahsilIds.map(String)} onChange={handleTalukaChange}>
                                {tahsils.map(t => (
                                  <MenuItemOption key={t._id} value={t._id} isDisabled={t.status === 'Approve' || t.status === "submitted"}>
                                    <Flex justify="space-between" w="100%">
                                      <Text>{t.name}</Text>
                                      <Tag size="sm" colorScheme={t.status === 'approved' ? 'green' : t.status === 'pending' ? 'orange' : t.status === 'submitted' ? 'blue' : 'gray'}>
                                        {t.status}
                                      </Tag>
                                    </Flex>
                                  </MenuItemOption>
                                ))}
                              </MenuOptionGroup>
                              <Box px={3} py={2} borderTopWidth="1px" bg="gray.50">
                                <Text fontSize="xs" color="gray.500">💡 Approved talukas cannot be selected</Text>
                              </Box>
                            </MenuList>
                          </Portal>
                        </Menu>
                      )}

                      {/* Selected tags */}
                      {useGodown ? (
                        selectedGodownIds.length > 0 && (
                          <Wrap spacing={1}>
                            {getSelectedGodownNames().map((name, i) => (
                              <Tag key={i} size="sm" colorScheme="teal" borderRadius="full">
                                <TagLabel>{name}</TagLabel>
                                <TagCloseButton onClick={() => {
                                  const g = godowns.find(g => (g.placeOfGodown || g.name) === name);
                                  if (g) setSelectedGodownIds(prev => prev.filter(id => id !== g._id));
                                }} />
                              </Tag>
                            ))}
                          </Wrap>
                        )
                      ) : (
                        selectedTahsilIds.length > 0 && (
                          <Wrap spacing={1}>
                            {getSelectedTalukaNames().map((name, i) => (
                              <Tag key={i} size="xs" colorScheme="teal" borderRadius="full">
                                <TagLabel fontSize="10px">{name}</TagLabel>
                                <TagCloseButton fontSize="8px" boxSize="12px" onClick={() => {
                                  const t = tahsils.find(t => t.name === name);
                                  if (t) setSelectedTahsilIds(prev => prev.filter(id => id !== t._id));
                                }} />
                              </Tag>
                            ))}
                          </Wrap>
                        )
                      )}
                    </Stack>
                  </Card>
                )}

                <Flex justify="flex-start" align="flex-end">
                  <Button colorScheme="teal" onClick={handleShowGrid} isLoading={loadingGrid} leftIcon={<FiDatabase />}>
                    Show Grid
                  </Button>
                </Flex>
              </SimpleGrid>
            )}

            {/* Show Grid button for empty records mode without location selector */}
            {scheme && fileType && shouldShowEmptyRecords() && (
              <Flex justify="flex-start">
                <Button colorScheme="teal" onClick={handleShowGrid} isLoading={loadingGrid} leftIcon={<FiDatabase />}>
                  Show Empty Grid (100 Records)
                </Button>
              </Flex>
            )}
          </Stack>

          {/* Grid */}
          {showGrid && (
            <Box mt={4}>
              <Flex justify="space-between" mb={3} wrap="wrap" gap={2}>
                <HStack>
                  <Button leftIcon={<FiRefreshCw />} size="sm" onClick={handleShowGrid}>Refresh</Button>


                  {scheme !== "68d135b1e18cfc3e5248edc2" && scheme !== "68ee2171ad9b09bdda13b4f6" && (<Button colorScheme="blue" disabled={!hasUnsavedChanges} leftIcon={<FiSave />} size="sm" onClick={(() => handleSaveNow("save"))} isLoading={saving}>
                    Save Now
                  </Button>)}
                </HStack>
                <HStack>
                  <Button variant="ghost" disabled={!hasUnsavedChanges} colorScheme="red" size="sm" onClick={handleDiscard} isDisabled={saving} leftIcon={<FiX />}>
                    Discard Changes
                  </Button>
                  <Button colorScheme="teal" disabled={!hasExistingDataWithSave && !hasUnsavedChanges} leftIcon={<FiUpload />} size="sm" onClick={() => handleSaveNow("upload")} isLoading={saving} loadingText="Uploading...">
                    Upload Data
                  </Button>
                </HStack>
              </Flex>

              {uploadProgress > 0 && uploadProgress < 100 && (
                <Box mb={3}>
                  <Text fontSize="xs">Uploading: {uploadProgress}%</Text>
                  <Box h="2px" bg="gray.200" borderRadius="full" overflow="hidden">
                    <Box h="full" bg="teal.500" width={`${uploadProgress}%`} transition="width 0.3s ease" />
                  </Box>
                </Box>
              )}

              <Box
                ref={gridContainerRef}
                className="ag-theme-alpine"
                tabIndex={0}
                sx={{
                  height: '600px',
                  width: '100%',
                  '& .ag-root-wrapper': { borderRadius: '8px' },
                  '& .ag-cell': { borderRight: '1px solid #e0e0e0' },
                  '& .ag-header': { backgroundColor: '#319795' },
                  '& .ag-header-cell': { backgroundColor: '#319795', color: '#ffffff', fontWeight: 600 },
                  '& .ag-header-cell-label': { whiteSpace: 'normal', lineHeight: '1.3', color: '#ffffff', textAlign: 'center' },
                }}
              >
                {loadingGrid ? (
                  <Flex justify="center" align="center" height="90%"><Spinner size="xl" /></Flex>
                ) : (
                  <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={{
                      sortable: true,
                      resizable: true,
                      editable: false,
                      minWidth: 90,
                      wrapHeaderText: true,
                      autoHeaderHeight: true,
                      singleClickEdit: true,
                      suppressKeyboardEvent: (params) => {
                        const { event, colDef } = params;
                        if (colDef?._isNumberField === true && colDef?.editable === true) {
                          if (event.key === '.' || event.key === '+' || event.key === '-' ||
                            event.key === 'e' || event.key === 'E') {
                            event.preventDefault();
                            event.stopPropagation();
                            return true;
                          }
                        }
                        return false;
                      },
                    }}
                    undoRedoCellEditing={true}
                    undoRedoCellEditingLimit={20}
                    copyHeadersToClipboard={true}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    animateRows={true}
                    enableClipboardApi={true}
                    clipboardDelimiter="\t"
                    suppressMovableColumns={true}
                    headerHeight={40}
                    onGridReady={onGridReady}
                    onCellValueChanged={onCellValueChanged}
                    onCellEditingStopped={onCellEditingStopped}
                    onCellKeyDown={onCellKeyDown}
                  />
                )}
              </Box>

              {/* <Alert status="info" mt={3} borderRadius="md" size="sm">
                <AlertIcon />
                <Box fontSize="sm">
                  <Text fontWeight="bold">💡 Tips:</Text>
                  <Text>• <strong>Ctrl+C</strong> to copy, <strong>Ctrl+V</strong> to paste from Excel</Text>
                  <Text>• Click a cell first, then paste — data fills from that cell downward</Text>
                  {schemaHasMonth() && <Text>• <strong>Month column</strong> is auto-populated from the month filter — not editable</Text>}
                  {schemaHasFormulas() && <Text>• <strong>Formula columns</strong> are auto-calculated and cannot be edited directly</Text>}
                  <Text>• <strong>Number columns</strong> only accept digits — a tooltip appears if you type letters or special characters</Text>
                  <Text>• <strong>Date columns</strong> use a date picker — selected dates are stored in dd-mm-yyyy format</Text>
                  <Text>• <strong>Auto-Save</strong> saves data locally every 3 seconds when changes are detected</Text>
                  <Text>• <strong>Ctrl+Z</strong> undo, <strong>Ctrl+Y</strong> redo within the grid</Text>
                  <Text>• Double-click editable cells to enter/edit values</Text>
                  <Text>• When you edit a source field, dependent formulas recalculate automatically</Text>
                  {shouldShowEmptyRecords() && <Text>• <strong>Empty Records Mode:</strong> 50 blank records ready for data entry - number fields start empty instead of 0</Text>}
                </Box>
              </Alert> */}
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
}

export default ExcelGridDataEntry;